// RUTA: src/lib/cleanReload.ts
//
// Helper compartido para "Recarga limpia" — borrar cache + service
// worker + recargar la app. Centralizado aquí porque se usa desde
// 3 lugares: StuckLoader, GlobalErrorBoundary, y eventualmente el
// menú de perfil.
//
// FILOSOFÍA: respetar al usuario. Si recargar limpio NO resuelve el
// problema en 3 intentos, NO le permitimos seguir intentando — lo
// canalizamos directo a crear un ticket P1. Eso evita loops de
// frustración y nos da visibilidad temprana de bugs reales.
//
// El contador de intentos vive en la URL (?_clr=N) en vez de
// localStorage porque el hard reset BORRA localStorage. Query params
// sobreviven al clear y al reload.

import { supabase } from '@/integrations/supabase/client';

const CLEAN_RELOAD_QUERY_PARAM = '_clr';

/** Cuántos intentos máximos permitimos antes de redirigir a ticket. */
export const MAX_CLEAN_RELOAD_ATTEMPTS = 3;

/** Tiempo máximo que esperamos al ping de Supabase antes de
 *  considerar la conexión "no apta" para una recarga limpia. */
const HEALTH_CHECK_TIMEOUT_MS = 3_000;

/**
 * Lee el contador actual de intentos desde la URL.
 * Devuelve 0 si no hay parámetro o es inválido.
 */
export function getCleanReloadAttempts(): number {
  if (typeof window === 'undefined') return 0;
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(CLEAN_RELOAD_QUERY_PARAM);
    if (!raw) return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

/**
 * Quita el parámetro `?_clr` de la URL silenciosamente. Llamar cuando
 * la app logra cargar bien después de un intento — así un cliente que
 * tuvo problemas hace 3 meses no se queda con el botón bloqueado por
 * los siguientes meses.
 */
export function resetCleanReloadAttempts(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(CLEAN_RELOAD_QUERY_PARAM)) return;
    url.searchParams.delete(CLEAN_RELOAD_QUERY_PARAM);
    window.history.replaceState(null, '', url.toString());
  } catch {
    // ignore — no es crítico
  }
}

/**
 * ¿Llegamos al máximo de intentos? Si sí, NO debemos ofrecer otro
 * botón de recarga limpia — en su lugar canalizamos al ticket.
 */
export function hasReachedCleanReloadCap(): boolean {
  return getCleanReloadAttempts() >= MAX_CLEAN_RELOAD_ATTEMPTS;
}

/**
 * Hace un health-check rápido contra Supabase para determinar si la
 * red real está OK (no solo `navigator.onLine`, que miente seguido
 * en cellular flaky). Si el ping responde en menos de 3s, la red
 * está apta. Si no, mejor no hacer la recarga limpia.
 */
export async function isConnectionHealthy(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return false;

  // Supabase auth.getSession es liviano (no toca BD, lee de storage
  // local + valida con servidor). Con timeout corto.
  const timeoutPromise = new Promise<false>((resolve) =>
    setTimeout(() => resolve(false), HEALTH_CHECK_TIMEOUT_MS),
  );
  const checkPromise = supabase.auth.getSession()
    .then(() => true as const)
    .catch(() => false as const);

  return Promise.race([checkPromise, timeoutPromise]);
}

/**
 * Ejecuta la recarga limpia:
 *   1. Bumpea el contador `?_clr=N` en la URL
 *   2. Desregistra todos los service workers
 *   3. Borra todos los caches del navegador (CacheStorage)
 *   4. Recarga la página con la URL actualizada
 *
 * No verifica conexión — eso debe hacerse ANTES por el caller con
 * `isConnectionHealthy()`. La razón de separarlas es que el caller
 * puede mostrar UI durante el check ("Verificando conexión…") y
 * decidir qué hacer si falla (mostrar mensaje vs proceder).
 */
export async function performCleanReload(): Promise<void> {
  if (typeof window === 'undefined') return;

  // 1. Bumpear contador en URL antes de borrar nada — garantiza que
  //    si algo falla y reinicia el navegador, el contador persista.
  const url = new URL(window.location.href);
  const current = getCleanReloadAttempts();
  url.searchParams.set(CLEAN_RELOAD_QUERY_PARAM, String(current + 1));
  const targetUrl = url.toString();

  // 2. Desregistrar service workers para que el siguiente fetch
  //    venga del network (no del SW cached).
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
  } catch {
    // ignore — seguimos al siguiente paso
  }

  // 3. Borrar todos los caches (workbox precache, runtime cache, etc.)
  try {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n).catch(() => false)));
    }
  } catch {
    // ignore
  }

  // 4. Borrar localStorage de la app — esto fuerza al perfil a
  //    refetchear desde red en vez de usar cache stale-while-revalidate.
  //    No tocamos auth tokens (sb-* keys) para que el usuario no
  //    tenga que volver a iniciar sesión. Filtramos solo nuestras
  //    keys propias (las que escribimos con `setCachedData`).
  try {
    const ourPrefixes = [
      'client_profile_', 'client_active_user_', 'client_is_legacy_',
      'onboarding_', 'profile_row_', 'goals_', 'measurements_',
      'routines_', 'day_titles_', 'meals_', 'supplements_',
      'food_logs_', 'cardio_', 'nutrition_history_', 'session_',
      'session_notes_', 'completion_logs_', 'weights_',
      'figure_gender_', 'macro_goals_', 'exercise_logs_',
      'client_access_',
    ];
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (ourPrefixes.some((p) => key.startsWith(p))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }

  // 5. Recargar a la URL con el contador actualizado.
  window.location.replace(targetUrl);
}
